import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import {
  SIDE_NAV_UPGRADE_CLICKED,
  UPGRADE_CLICKED,
} from "constants/analyticsEvents";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { useUpgradePlan } from "hooks/useUpgradePlan";
import { ComponentProps, ReactNode } from "react";
import { SUBSCRIPTION_PERMISSION_FEATURES } from "types/contants/subscriptionPermissionFeaturesTypes";

export const PermissionBasedUI = ({
  children,
  blockedUI,
  feature,
  errorMessage,
  route,
  product_type = "BOOKS",
  entityId,
  onUpgrade,
  showTooltip = true,
  ...props
}: {
  children: ReactNode;
  feature?: SUBSCRIPTION_PERMISSION_FEATURES;
  route?: string;
  blockedUI?: ReactNode;
  errorMessage?: string;
  product_type?: "TAX" | "BOOKS";
  entityId?: string;
  showTooltip?: boolean;
  onUpgrade?: () => void;
} & ComponentProps<typeof ToolTip>) => {
  const permission = useHasSubscriptionPermission({
    feature: feature,
    route,
    product_type,
    parentEntityId: entityId,
  });
  const { upgrade } = useUpgradePlan();
  const { currentApp } = useCurrentAppContext();
  const { trackEvent } = useAnalytics();

  const blocked = permission?.blocked;

  if (!blocked) {
    return children;
  }

  if (!permission) {
    return children;
  }

  const handleUpgrade = (
    event?: React.MouseEvent | React.SyntheticEvent | Event
  ) => {
    event?.stopPropagation();
    if (route) {
      trackEvent(SIDE_NAV_UPGRADE_CLICKED, {
        name: route,
        app_name: currentApp.name,
      });
    }
    if (!route && feature) {
      trackEvent(UPGRADE_CLICKED, {
        from: feature,
      });
    }
    if (permission.subscription) {
      upgrade({ addon: permission.subscription });
    }
  };

  return (
    <>
      <ConditionalToolTip
        keepOpenOnActivation={true}
        {...props}
        condition={
          showTooltip && (
            <div className="t-flex t-flex-col t-gap-4 t-text-left t-w-[264px]">
              <div className="t-flex t-flex-col t-gap-2">
                <p className="t-m-0 t-text-subtitle-sm">Upgrade to unlock</p>
                <p className="t-m-0 t-text-body-sm t-break-keep">
                  {errorMessage || permission.message}
                </p>
              </div>
              <Button onClick={onUpgrade || handleUpgrade} size="small">
                Upgrade
              </Button>
            </div>
          )
        }
      >
        <span role="button">{blockedUI || children}</span>
      </ConditionalToolTip>
    </>
  );
};
